import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { motion } from "framer-motion";
import { useState } from "react";
import { 
  GraduationCap, 
  Search, 
  Scale, 
  Laptop,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Phone,
  MessageCircle,
  ClipboardList,
  UserCheck,
  FileCheck,
  AlertTriangle,
  ShieldAlert
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CTASection } from "@/components/home/CTASection";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const iconMap = {
  trainings: GraduationCap,
  "risk-assessment": Search,
  "safety-plan": ClipboardList,
  consulting: Scale,
  "external-officer": UserCheck,
  inspections: FileCheck,
  "incident-investigation": AlertTriangle,
  digital: Laptop,
  emergency: ShieldAlert,
};

const colorMap = {
  trainings: "from-amber-500/20 to-orange-500/10",
  "risk-assessment": "from-blue-500/20 to-indigo-500/10",
  "safety-plan": "from-green-500/20 to-emerald-500/10",
  consulting: "from-emerald-500/20 to-teal-500/10",
  "external-officer": "from-purple-500/20 to-violet-500/10",
  inspections: "from-red-500/20 to-rose-500/10",
  "incident-investigation": "from-orange-500/20 to-amber-500/10",
  digital: "from-violet-500/20 to-purple-500/10",
  emergency: "from-cyan-500/20 to-blue-500/10",
};

interface ServiceItem {
  id: string;
  title: string;
  description: string;
  fullDescription: string;
  features: string[];
}

const Services = () => {
  const { t, i18n } = useTranslation('services');
  const isRTL = i18n.language === 'he';
  const services = t('services', { returnObjects: true }) as ServiceItem[];
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(null);

  return (
    <Layout>
      <Helmet>
        <title>שירותי בטיחות – ייעוץ, סקרי סיכונים והדרכות</title>
        <meta name="description" content="שירותי בטיחות מקצועיים: ממונה בטיחות חיצוני, סקרי סיכונים, הדרכות, חקירות תאונות וליווי רגולטורי לעסקים בישראל." />
        <link rel="canonical" href="https://adam-safety.com/services" />
        <meta property="og:title" content="שירותי בטיחות – ייעוץ, סקרי סיכונים והדרכות" />
        <meta property="og:description" content="ממונה בטיחות, סקרי סיכונים, הדרכות וליווי רגולטורי לארגונים בישראל." />
        <meta property="og:url" content="https://adam-safety.com/services" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "שירותי בטיחות בעבודה",
          "provider": { "@type": "LocalBusiness", "name": "אדם בטיחות", "url": "https://adam-safety.com" },
          "areaServed": { "@type": "Country", "name": "Israel" },
          "url": "https://adam-safety.com/services"
        })}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-background" />
        <div className="absolute inset-0 pattern-grid" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block text-accent font-bold text-sm uppercase tracking-widest mb-4 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
              {t('hero.badge')}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              {t('hero.title')}
              <span className="text-gradient-accent"> {t('hero.titleAccent')}</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              {t('hero.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding pt-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {services.map((service, index) => {
              const Icon = iconMap[service.id as keyof typeof iconMap] || GraduationCap;
              const color = colorMap[service.id as keyof typeof colorMap] || "from-gray-500/20 to-gray-500/10";
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedService(service)}
                >
                  <div className={`relative rounded-xl p-3 bg-gradient-to-br ${color} border border-border/50 h-full hover:shadow-md transition-all duration-300 hover:-translate-y-1`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-card/80 flex items-center justify-center shadow-sm">
                        <Icon className="w-4 h-4 text-accent" />
                      </div>
                    </div>
                    <h3 className="font-heading text-sm font-bold text-foreground mb-1">
                      {service.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Detail Dialog */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-lg bg-card border-border">
          {selectedService && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorMap[selectedService.id as keyof typeof colorMap] || "from-gray-500/20 to-gray-500/10"} flex items-center justify-center`}>
                    {(() => {
                      const Icon = iconMap[selectedService.id as keyof typeof iconMap] || GraduationCap;
                      return <Icon className="w-6 h-6 text-accent" />;
                    })()}
                  </div>
                  <DialogTitle className="font-heading text-xl font-bold text-foreground">
                    {selectedService.title}
                  </DialogTitle>
                </div>
                <DialogDescription className="text-muted-foreground text-base leading-relaxed">
                  {selectedService.fullDescription}
                </DialogDescription>
              </DialogHeader>
              
              <div className="mt-4">
                <h4 className="font-semibold text-foreground mb-3">{t('dialog.includes')}</h4>
                <ul className="space-y-2">
                  {selectedService.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                      <span className="text-foreground/80">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-3 mt-6">
                {selectedService.id === "trainings" ? (
                  <Button asChild className="flex-1">
                    <Link to="/trainings">
                      {t('dialog.moreInfo')}
                      {isRTL ? <ArrowLeft className="mr-2 w-4 h-4" /> : <ArrowRight className="ml-2 w-4 h-4" />}
                    </Link>
                  </Button>
                ) : (
                  <Button asChild className="flex-1">
                    <Link to="/contact">
                      <Phone className={isRTL ? "ml-2 w-4 h-4" : "mr-2 w-4 h-4"} />
                      {t('dialog.contact')}
                    </Link>
                  </Button>
                )}
                <Button asChild variant="outline">
                  <a href="https://wa.me/972501234567" target="_blank" rel="noopener noreferrer">
                    <MessageCircle className={isRTL ? "ml-2 w-4 h-4" : "mr-2 w-4 h-4"} />
                    {t('dialog.whatsapp')}
                  </a>
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CTASection />
    </Layout>
  );
};

export default Services;